import { Link, createFileRoute, notFound } from "@tanstack/react-router";
import { z } from "zod";

import { CihClosingCta } from "~/components/cih/closing-cta";
import {
  CihCoachCellGrid,
  toCihCoachCell,
  type CihCoachCellData,
} from "~/components/cih/coach-cell";
import { CihPagination } from "~/components/cih/pagination";
import { Breadcrumb } from "~/components/ui/breadcrumb";
import { Button } from "~/components/ui/button";
import {
  getCoachesByChallengeFn,
  getPublishedChallengeWithRelationsFn,
  type ChallengeWithRelations,
} from "~/lib/functions/cih/challenges";
import { createBrandMeta } from "~/lib/shared/meta";

const PAGE_SIZE = 24;

const searchSchema = z.object({
  page: z.number().int().min(1).optional(),
});

type LoaderData = {
  title: string;
  challenge: ChallengeWithRelations;
  coaches: CihCoachCellData[];
  total: number;
  page: number;
};

export const Route = createFileRoute(
  "/_public/coaching-for/$challengeSlug/coaches",
)({
  staticData: { title: "Coaches" },
  validateSearch: searchSchema,
  loaderDeps: ({ search }) => ({ page: search.page ?? 1 }),
  loader: async ({ params, deps }): Promise<LoaderData> => {
    const challenge = await getPublishedChallengeWithRelationsFn({
      data: { slug: params.challengeSlug },
    });
    if (!challenge) throw notFound();
    const result = await getCoachesByChallengeFn({
      data: {
        challengeId: challenge.id,
        page: deps.page,
        limit: PAGE_SIZE,
      },
    });
    return {
      title: `Coaches for ${challenge.title}`,
      challenge,
      coaches: result.coaches.map(toCihCoachCell),
      total: result.total,
      page: result.page,
    };
  },
  head: ({
    loaderData,
    params,
  }: {
    loaderData?: LoaderData;
    params: { challengeSlug: string };
  }) => {
    const title = loaderData?.challenge.title ?? params.challengeSlug;
    const page = loaderData?.page ?? 1;
    const pageSuffix = page > 1 ? ` — Page ${page}` : "";
    return createBrandMeta({
      title: `${title} coaches${pageSuffix} | Dream Coach Match`,
      description: `All coaches who can help with ${title}.`,
      ogUrl: `https://dreamcoachmatch.com/coaching-for/${params.challengeSlug}/coaches`,
      canonical: `https://dreamcoachmatch.com/coaching-for/${params.challengeSlug}/coaches`,
    });
  },
  component: ChallengeCoachesPage,
});

function ChallengeCoachesPage() {
  const { challenge, coaches, total, page } = Route.useLoaderData();
  const { challengeSlug } = Route.useParams();
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  const intakeSearch = {
    source: `challenge:${challengeSlug}:coaches`,
    ...(challenge.intakeTrigger ? { trigger: challenge.intakeTrigger } : {}),
  };

  const buildHref = (p: number) =>
    p === 1
      ? `/coaching-for/${challengeSlug}/coaches`
      : `/coaching-for/${challengeSlug}/coaches?page=${p}`;

  return (
    <>
      <section className="border-b border-gray-200 bg-white">
        <div className="mx-auto max-w-7xl px-4 pt-8 pb-12 md:pt-10 md:pb-16 xl:px-0">
          <Breadcrumb
            items={[
              { label: "Home", href: "/" },
              { label: "Coaching for", href: "/coaching-for" },
              {
                label: challenge.title,
                href: `/coaching-for/${challengeSlug}`,
              },
              { label: "Coaches" },
            ]}
            isBold={false}
          />
          <div className="mt-6 text-xs font-medium tracking-[0.22em] text-purple-700 uppercase">
            ● {challenge.title}
          </div>
          <h1 className="mt-4 font-heading text-3xl font-medium tracking-tight text-gray-900 md:text-4xl lg:text-5xl xl:text-6xl">
            {total} coaches who can help with {challenge.title}
          </h1>
          <div className="mt-6 flex flex-wrap gap-6 text-sm">
            <Link
              to="/coaching-for/$challengeSlug/articles"
              params={{ challengeSlug }}
              className="font-medium text-purple-700 hover:text-purple-800"
            >
              Read articles on {challenge.title} →
            </Link>
          </div>
        </div>
      </section>

      <div className="mx-auto max-w-7xl px-4 py-14 md:py-20 xl:px-0">
        <CihCoachCellGrid coaches={coaches} />
      </div>

      <CihPagination
        page={page}
        totalPages={totalPages}
        buildHref={buildHref}
      />

      <CihClosingCta
        headline="Your next chapter starts with a question."
        cta={
          <Link to="/intake" search={intakeSearch}>
            <Button variant="primary" size="lg">
              Start the conversation →
            </Button>
          </Link>
        }
      />
    </>
  );
}
